/**
 * What a tenant administrator can actually reach, and what this console cannot yet draw.
 *
 * The mechanism is the dispatch console's, deliberately re-stated rather than imported — see
 * `docs/adr/0021-two-consoles-and-no-shared-shell.md` for why nothing is shared between the two
 * apps yet. The three questions it answers are the same three:
 *
 *   - visible   — show it in the rail
 *   - reachable — allow it to be opened
 *   - readOnly  — open it, but do not offer the commands
 *
 * What is different here is the proportion. `17 WP16 Tenant Admin.dc.html` draws fifteen pages and
 * **three of them have no operation a browser can call** — eleven when this file was written, then
 * six, and the eight that moved are named below. That is not a gap in this file: it is the measured
 * state of the services behind a tenant admin suite, and every one of the three that remain carries
 * the reason it is blocked. A page missing from this list would look like an oversight; a page with a
 * measured reason attached is a piece of work somebody can pick up.
 *
 * **No count of blocked pages is written anywhere but in the sentence above, and it is the sentence
 * `tests/page-support.test.ts` re-derives.** That file measures which pages a listing exists for and
 * fails when this one disagrees, which is the only reason the number above is safe to write down.
 *
 * Nothing here talks to a service. Scopes arrive in the access token, which is the only authority
 * on what an administrator may do, and this module decides only what to draw. Every command still
 * goes to a service that checks again.
 */

export interface PageDefinition {
  readonly id: string;
  /** The page's own title, as the design's `PAGES` table writes it. */
  readonly title: string;
  /**
   * The rail's label, where the design's menu uses a shorter one than the page title.
   *
   * Three of the fifteen differ: the menu says `Staff`, `Integrations` and `Audit` where the pages
   * are titled `Staff and roles`, `Integrations and webhooks` and `Tenant audit`. Both are the
   * design's own words, so both are kept rather than one being chosen.
   */
  readonly railLabel?: string;
  /** The design's `NAV` group this item sits in, in that file's own words. */
  readonly section: string;
  /** Read requirements, OpenAPI's shape: any of these alternatives, all scopes within one. */
  readonly readRequirements: readonly (readonly string[])[];
  readonly writeRequirements?: readonly (readonly string[])[];
  /** Why the page exists, in the administrator's terms — the design's own `PAGES` description. */
  readonly purpose: string;
  /** Why this page cannot be built yet, if it cannot. */
  readonly blockedBy?: string;
  /** The services this page reads from, by their canonical identifiers. */
  readonly owners: readonly string[];
  /** The Material Symbols Rounded glyph, read from `NAV` in the design file. */
  readonly glyph: string;
  /**
   * True for a page the design file does not draw, built because the client asked for it.
   *
   * **It exists so that "matches the design" stays a checkable claim.** Fifteen pages here are
   * reproductions of `17 WP16 Tenant Admin.dc.html` and can be held against it pixel for pixel;
   * the analytics pages have no reference, so asserting all nineteen against the design file would
   * quietly weaken the assertion that matters. `navigation.test.ts` checks the designed fifteen
   * against the design and the commissioned four against this flag, which keeps the two kinds of
   * page from being confused for one another.
   *
   * Recorded as Q-186 in `docs/design/DESIGN_QUESTIONS.md`, with what a design pass would change.
   */
  readonly commissioned?: true;
}

export type PageAccess = "full" | "read_only" | "forbidden";

export interface ResolvedPage {
  readonly page: PageDefinition;
  readonly access: PageAccess;
  readonly visible: boolean;
}

/**
 * The tenant admin console's pages, in the design file's own order and groups.
 *
 * Each `blockedBy` was measured against `docs/design/api-operations.json` at the commit that
 * catalogue is stamped with, and the shape of the measurement is always the same question: **is
 * there an operation a browser holding a tenant administrator's token can call that answers a list
 * of the things this page is a list of?** Four distinct ways the answer is no, and they are worth
 * separating because they need different fixes:
 *
 *  1. **The service is internal.** Every operation is under `/internal/v1`, which the gateway does
 *     not expose, so no console can call it however it is scoped. **None any more.** It was three —
 *     `templates`, `integrations` and `flags` — and ADR 0029 §2 is why the gateway was never the
 *     answer: EXP-001 refuses at load any route whose `pathPrefix` begins `/internal`, so each
 *     needed a public surface decided by the service that owns it. ADRs 0043, 0044 and 0045 are
 *     those three decisions, and R-55 is the measurement.
 *  2. **There is a read, and it answers one row.** The operation needs an identifier the page
 *     exists to discover. None any more, and this is the case that nearly survived case 1: all
 *     three of the services above also had this fault *inside* the first one, so publishing a route
 *     would have left each page with nothing to ask for. Each published a listing instead.
 *  3. **There is a list, and its rows have no published shape.** None any more.
 *  4. **The service does not exist, or the platform models nothing the page is a list of.** Two
 *     pages, and they are the two where a listing was route work and would still not have drawn
 *     the board: see `channels` and `staff` below, and R-39. Plus `roles`, which ADR 0016 defers
 *     outright — its read requirement list is empty rather than unmet, because there is no read
 *     scope to name.
 *
 * **Five of the eleven moved out of this list in R-39**, when the listing each was short of was
 * served: `pricing`, `customers`, `plan`, `policies` and `config`. "The platform serves no list"
 * stopped being true of them, and a page left blocked after its listing is published reads exactly
 * like a page the platform never supported — that is what R-17 cost and why
 * `tests/page-support.test.ts` re-derives this rather than trusting it.
 *
 * **R-40 drew all five**, so nine of the fifteen were routed. **R-55 served and drew the last three
 * cases of 1 and 2** — `templates`, `integrations` and `flags` — so twelve are routed. None of the
 * eight is *complete*: each carries, below, the designed column or tile its listing cannot fill, and
 * each is drawn as the design's own absence rather than invented or dropped.
 *
 * Recorded as R-12 and R-13, re-measured in R-39, and re-measured again in R-55, in
 * `docs/design/DESIGN_RECONCILIATION.md`.
 */
export const tenantAdminPages: readonly PageDefinition[] = [
  {
    id: "brand",
    glyph: "palette",
    owners: ["TEN-04"],
    title: "Brand and domains",
    section: "Brand and channels",
    purpose:
      "Logo, colours and the web address customers see. Colour choices are checked for contrast before they go live.",
    // Built. TEN-04 answers `GET /v1/themes` — a real tenant-scoped list, publishing `key`,
    // `version`, `displayName`, `colors`, `assets`, `createdAt` and `createdBy` on every row. R-12
    // named it reachable; R-16 re-read the contract and the service to confirm it before building,
    // and found two things that reading only the catalogue would not have.
    //
    // **Nothing on this platform checks a brand colour's contrast, and this page's own designed
    // sentence says something does.** The write validates a six-digit hex, an HTTPS asset URL
    // without credentials, and a display name's length; `nextTheme()` validates only the version.
    // So the console does the check, which design rule 6 requires of it anyway, and says where the
    // check happened.
    //
    // **The listing's cap is invisible.** It publishes no parameters and no cursor, and the
    // repository runs `LIMIT 200` ordered by `theme_key`. A truncated answer is indistinguishable
    // from a complete one, which is why there is no count anywhere on the page.
    //
    // The design also cites the entitlement service for this page, and that half is case 2 — an
    // entitlement is read one capability at a time, by a name the page would have to already know,
    // and the path parameter is `type: string` with no enum, so there is no vocabulary to
    // enumerate. The owner list is TEN-04 alone and the KPI reading `Plan · Growth` has no source.
    readRequirements: [["tenant-branding:read"]],
    writeRequirements: [["tenant-branding:write"]],
  },
  {
    id: "channels",
    glyph: "devices",
    owners: ["TEN-05"],
    title: "Channels",
    section: "Brand and channels",
    purpose: "Where customers can book: app, website, WhatsApp, phone and partner APIs.",
    // Case 4, and the half of it that a route cannot fix. The design names the organization-hierarchy
    // service for a page about booking surfaces — an app build, a website, a WhatsApp number, a phone
    // desk — and that service models organisation units: an organization, a branch, an operating
    // unit, each with a name, a parent and a status.
    //
    // **TEN-05's missing listing is real route work and was deliberately not served.** R-39 served
    // the five listings whose rows are the rows their board draws, and stopped here: the design's
    // grid is `Channel · Surface · Last booking` over `Rider app (Android) · Play Store 4.12.0`, and
    // a page of organisation units is a list of different things, not a list of channels with two
    // columns missing. Publishing it would have made `page-support.test.ts` report this page as no
    // longer blocked, which would be the first thing a reader believed and the wrong thing.
    //
    // So what this page needs is a channel: an aggregate, an owner and a decision about which
    // service holds it. Recorded in `docs/OPEN_WORK.md` with the organisation-unit listing beside
    // it, because that listing is worth serving on its own account — a tenant's hierarchy is
    // write-only today, readable one `unitId` at a time and only by whoever kept the identifier the
    // create returned.
    /*
     * **Served by writing the record, and its recorded reason was right about both halves.**
     *
     * The entry that stood here read: "Nothing on the platform models a booking channel. TEN-05
     * holds organisation units, and a listing of those — which it also lacks — would be a list of
     * different things, not this board with columns missing."
     *
     * Re-measured before a table was written, the way ADR 0088 asks, and nothing else held one:
     * no contract on this platform mentions a booking channel, and the only thing resembling one
     * is the set of OAuth clients in Keycloak, which is how an application authenticates rather
     * than what a tenant offers. ADR 0091 adds the aggregate to TEN-05 — not as an organisation
     * unit, but as the same **kind** of record: a part of a tenant an administrator adds, names and
     * turns on or off.
     *
     * **Still degraded.** `Last booking` is empty, because nothing links a booking to the channel
     * it arrived through — a field on two other services' aggregates rather than a column here.
     * The design's `in store review` badge is not drawn either: an app store's queue is not this
     * platform's to report.
     *
     * The scope moved from `organization:read` to one of its own. An administrator who may read
     * the org chart is not automatically one who may see every way customers reach the business,
     * and ADR 0032 grants a channel client only the scopes its own screens spend — so a wrong
     * constant here is a wrong grant on the client.
     */
    readRequirements: [["booking-channel:read"]],
  },
  {
    id: "catalogue",
    glyph: "category",
    owners: ["MOB-01"],
    title: "Service catalogue",
    section: "Offer",
    purpose: "Ride classes, delivery types and what each one offers.",
    // Built, and **R-12 had this page under case 4 and the rejection was wrong in a useful way.**
    //
    // Both halves of that rejection are still true: the two codes the design cites for this page
    // are answered by none of the 142 published contracts. What the question did not ask is
    // whether a service the design does *not* cite answers the page's own question — and MOB-01
    // `GET /v1/ride-products` does. Public, tenant-scoped, one read scope, publishing
    // `displayName`, `vehicleClass`, `travelMode`, `passengerCapacity`, `luggageCapacity`,
    // `serviceAreaIds`, `lifecycle`, `effectiveFrom` and `effectiveUntil` per row. Two of the
    // design's three columns are exact and the third is the design's own grammar: `4 seats · 2
    // bags` is the two capacities. R-17.
    //
    // **`lifecycle` is a constant on this operation.** The listing selects `lifecycle='active'` and
    // the effective window, so no draft and no retired product ever appears, while the contract
    // publishes the field as `draft | active | retired`. A state column fed by it confirms "active"
    // for every row of a catalogue that is missing records, which is why the board says under its
    // grid what the listing is a list of.
    //
    // **`serviceAreaIds` restricts nothing.** No service under `services/` or `packages/` acts on
    // the field. The design's `caution · 3 zones only` badge would state a restriction the platform
    // does not enforce, so the badge carries the measured fact instead. EXP-004 reads it — its offer
    // aggregate resolves each identifier to the zone GEO-06 names, which is this element's first
    // source — and enforces nothing, which is why the badge is unchanged.
    //
    // Half the `Type` column has no source. The service that owns delivery products publishes no
    // listing at all — its one read needs a `productId` the page exists to discover *and* a
    // `geographyId`, which no operation on this platform ever answers, only ever accepts. Its code
    // is deliberately not written here: it is not a service this page reads from, and
    // `tests/service-codes.test.ts` holds every code in this file to being one.
    readRequirements: [["ride-product:read"]],
    writeRequirements: [["ride-product:write"]],
  },
  {
    id: "pricing",
    glyph: "sell",
    owners: ["PRC-01"],
    title: "Pricing and rate cards",
    section: "Offer",
    purpose: "Rate cards are versioned. A new version needs an approver before it takes effect.",
    // Served in R-39. `GET /v1/tenants/{tenantId}/rate-cards` answers every version a tenant holds,
    // newest first, publishing `name`, `version`, `status`, `currency`, `components`, `effectiveFrom`,
    // `effectiveTo`, `approvedAt`, `createdAt` and a `nextAfter`, with optional `status` and
    // `productId` filters — so the design's own headline row, `v13 · awaiting approval`, is reachable
    // for the first time. It was the sharpest of the eleven absences: the two reads this service had
    // were one version by `(rateCardId, version)` and the single card *effective* for a triple, and a
    // card awaiting approval is effective at no instant, so no parameter would have returned it.
    //
    // **The `Scope` column is a product identifier, not a product name.** The design writes `all ride
    // services`; PRC-01 publishes `productId` as a uuid and nothing else, and the service that names a
    // ride product is MOB-01, which is not an owner of this page. So the column carries the identifier
    // and says so, rather than a name chosen here. Recorded as Q-102.
    //
    // Two of the four KPI tiles have no source either: `Surge zones now · 2` belongs to the
    // dynamic-pricing service, whose policy read is by `policyId`, and `Promotions · 4` to the
    // promotion service. Neither is an owner of this page, so neither is named by code here —
    // `service-codes.test.ts` holds every code in this file to being one.
    readRequirements: [["pricing:configuration:read"]],
    writeRequirements: [["pricing:configuration:write"]],
  },
  {
    id: "zones",
    glyph: "map",
    owners: ["GEO-06"],
    title: "Zones and coverage",
    section: "Offer",
    purpose: "Service areas, partial coverage and the reason shown to customers outside them.",
    // Built. `GET /v1/service-areas` is a keyset board over a tenant's areas, strongest precedence
    // first, publishing `name`, `marketRef`, `productRefs`, `priority`, `lifecycle`,
    // `effectiveFrom`, `effectiveUntil`, `boundary`, `version` and a `nextAfter` — three of the
    // design's three columns and a state, which is what decided this screen over the other twelve.
    //
    // Two scopes is not what this costs: the read scope alone opens both the listing and the
    // by-identifier read, unlike the dispatch console's boards where a listing demanded a second
    // workload scope. So a tenant administrator holding `service-area:read` sees the page.
    //
    // The design's state vocabulary is *not* this service's, and that is R-13 rather than a detail:
    // `lifecycle` is draft, active and inactive, and the design's badges read served, partial and
    // not served. They are not the same question, and the one the design cares most about — partial
    // coverage, which is the page's stated purpose — has no field anywhere on the row.
    readRequirements: [["service-area:read"]],
    writeRequirements: [["service-area:write"]],
  },
  {
    id: "staff",
    glyph: "group",
    railLabel: "Staff",
    owners: ["IAM-02"],
    title: "Staff and roles",
    section: "People",
    purpose: "Who can sign in to your consoles and what they can do.",
    /*
     * **This was case 4 and it is now served.** The entry that stood here is kept below because it
     * is the one blocked reason on this console that was right about everything and still named the
     * wrong fix — and because it says what it would take, which is what got done.
     *
     * > IAM-02 federates identity providers and links external subjects to them; it holds no staff
     * > member, no name, no role and no last-sign-in. A subject-link listing is route work and was
     * > deliberately not served: `GET /v1/tenants/{tenantId}/subject-links` could be served
     * > honestly — and all three of the design's columns would still be empty. Recorded in
     * > `docs/OPEN_WORK.md` with what it would take: **a workforce member somewhere, and a decision
     * > about which service holds one.**
     *
     * ADR 0084 is that decision: `workforce_members` in IAM-02, a name and a role and a place, with
     * `GET /v1/tenants/{tenantId}/workforce-members` over it. Two of the three columns are now
     * published and `Last sign-in` is not — `src/staff.ts` carries each absence with its reason,
     * and `ui/Staff.tsx` draws them.
     *
     * **The scope changed with it.** This used to declare `workforce:subject:resolve`, which IAM-02
     * does enforce and which is the wrong grant: resolving a federated subject is what a service
     * does during sign-in, and a sign-in path holding this read could enumerate a tenant's whole
     * staff. `workforce:member:read`, per ADR 0084 §6.
     *
     * The design's own console tips point an operator at this page — "a tenant administrator links
     * it in Staff and roles" is what the sign-in screen tells somebody whose identity has no
     * platform account — so a page that could not be drawn was a dead end the chrome advertised.
     */
    readRequirements: [["workforce:member:read"]],
  },
  {
    id: "roles",
    glyph: "key",
    owners: ["IAM-03"],
    title: "Roles and permissions",
    section: "People",
    purpose:
      "Custom roles for your team. Sensitive permissions are marked and need a second person.",
    /*
     * This was case 2, and the most one-sided of them: IAM-03 published a decision endpoint and two
     * writes — create a policy, replace a policy — and **no read of a policy at all**, so a role
     * could be written and never looked at. `readRequirements` was empty, and the comment here said
     * the emptiness was the finding rather than a placeholder, because there was no read scope to
     * name.
     *
     * ADR 0080 publishes the two reads and the scope. The note was accurate, and what it did not
     * say is that the repository had held `listForTenant` and `findById` all along — `decide`
     * evaluates every policy a tenant has — so the absence was one layer thick.
     *
     * **The write is deliberately not declared**, which is the part worth reading twice. ADR 0032
     * grants a channel client exactly the scopes its own screens name, so declaring
     * `authorization:policy:write` here would put the permission to rewrite a tenant's
     * authorization policies on every tenant administrator's token — and the thing those policies
     * decide is what that administrator may do. A board that could rewrite its own permissions is
     * the one write on this console worth refusing on principle rather than for a missing field.
     *
     * The consequence is that `resolvePage` answers `read_only` and the page reads "View only",
     * which is true: `Create role` is drawn and refused, and its refusal says so. The designed form
     * could not be filled either — its three chip groups name none of the four things a rule
     * requires — but that is the second reason, not the first.
     */
    readRequirements: [["authorization:policy:read"]],
  },
  {
    id: "customers",
    glyph: "person_search",
    owners: ["CUS-01"],
    title: "Customers",
    section: "People",
    purpose:
      "Your riders and senders. Personal data is masked until you open a record, and opening it is audited.",
    // Served in R-39. `GET /v1/customer-profiles` answers a tenant-scoped page of customers, newest
    // first, publishing `id`, `identitySubjectId`, `displayName`, `locale`, `status`, `version`,
    // `createdAt`, `updatedAt` and a `nextAfter`, with optional `status` and `identitySubjectId`
    // filters — the second of which is the lookup that was also missing, and is how a signed-in
    // subject is resolved to a record at all.
    //
    // **Two scopes, and this is the one page of the fifteen whose listing costs a scope nothing else
    // needed.** `customer-profile:read` is an optional client scope on `tdelivr-customer-app` — a
    // rider's own phone — so a roster granted to every holder of it would hand a phone every
    // customer's name in the tenant. The route requires `customer-profile:roster` in addition, which
    // no mobile client holds. ADR 0016 gains one entry; the reasoning is the driver roster's own
    // second scope, `driver-profile:workload`, and the opposite conclusion to ADR 0027 §2 for the
    // reason that ADR gives — there the caller about to hold the scope was unchanged, and here it
    // is not.
    //
    // **`Phone` has no source.** The design's second column is `+254 7•• ••• 421` and CUS-01 holds no
    // telephone number: a profile is an `identitySubjectId`, a display name, a locale and a status.
    // Nothing reachable publishes a customer's phone number or email under any name — the search was
    // over every contract, not over this one. Recorded as Q-103.
    //
    // None of the four KPI tiles has a source. `182,440` and `3,902` are counts no operation answers;
    // `Deletion requests · 4` is nearest to `closure_pending`, and the design's "30-day clock" is a
    // legal deadline this service does not hold; `Blocked · 61` spans `restricted` and `suspended`,
    // which are two states and one tile.
    readRequirements: [["customer-profile:read", "customer-profile:roster"]],
    writeRequirements: [["customer-profile:create"]],
  },
  {
    id: "templates",
    glyph: "sms",
    owners: ["NTF-07"],
    title: "Messages and templates",
    section: "Connect",
    purpose: "SMS, push, email and WhatsApp messages your customers and drivers receive.",
    // Served in R-55 by ADR 0043, and this page carried **both** of the faults listed separately
    // above.
    //
    // It was case 1: every one of NTF-07's five operations — create, read a version, publish,
    // retire, render — was under `/internal/v1`, which ADR 0029 §2 says EXP-001 refuses at load
    // rather than rewrites. And inside that it was case 2 twice over: the one read among the five
    // needs a `templateKey` **and** a `version`, both of which a board of a tenant's templates
    // exists to discover. So the remedy had to be a listing rather than a route, and NTF-07 now
    // publishes `GET /v1/notification-templates` — `templateKey`, `version`, `locale`, `channel`,
    // `status`, `createdAt` and `updatedAt` on every row, paged over the variant triple, under a
    // scope of its own.
    //
    // **The message body is deliberately not published.** `content`, `subject` and
    // `allowedVariables` stay on the workload surface, and so does the render operation that
    // interpolates a customer's data into a body. The board draws none of them.
    //
    // **The design names the wrong two services for this page**, and that half of the old reason
    // still stands: its chip cites the SMS adapter and the notification-preference service, and
    // neither holds a template — one owns send attempts and the other owns one user's preference per
    // topic. Their codes are deliberately not spelled here, because this file names no service it
    // does not read from, even in prose; `tests/service-codes.test.ts` enforces that, and Q-149
    // spells them. The built page's chip reads NTF-07.
    //
    // Two things the design draws have no source: `sw missing`, which is a comparison against a set
    // of required languages nobody publishes, and `WhatsApp`, which is not one of NTF-07's four
    // channels. Q-152. Neither is a column, and none of the four tiles has a source.
    readRequirements: [["template:summary:read"]],
  },
  {
    id: "integrations",
    glyph: "hub",
    railLabel: "Integrations",
    owners: ["API-02"],
    title: "Integrations and webhooks",
    section: "Connect",
    purpose: "Keys for your own systems and the webhooks we send them.",
    // Served in R-55 by ADR 0044, and this is the one of the three where the published projection is
    // the security boundary rather than a narrowing.
    //
    // It was case 1 with case 2 inside it, like `templates`: issue, transition and introspect were
    // all under `/internal/v1`, and all three take a `partnerId` minted by whoever registered the
    // partner — the identifier a board of a tenant's integrations exists to discover. API-02 now
    // publishes `GET /v1/partner-credentials` across every partner in the tenant.
    //
    // **What it publishes is a mask and a state.** `displayName`, `status`, `expiresAt`,
    // `revokedAt`, `version`, `createdAt`, `updatedAt` and `clientIdSuffix` — the last four
    // characters of the client identifier, truncated in SQL. Withheld: the client identifier, the
    // argon2id hash of the secret, the key-store reference, the two approval references and the
    // granted scopes. The introspection operation, which takes a plaintext secret and answers
    // whether it is live, is not published at all.
    //
    // **The design's note is half wrong and the board says the true half.** "A secret is shown once
    // when created, then only its last four characters" — the first clause is exactly API-02's
    // behaviour; the second is not, because what is kept is a hash. Q-150.
    //
    // **The `Webhook` column has no source**, and it is a service boundary rather than a missing
    // field: those values are the webhook-delivery service's own outcomes, that service publishes
    // only internal operations, and it is not an owner of this page. Its code is deliberately not
    // spelled here, as the design's other mis-citations are not; Q-153 spells it. Drawn as the
    // design's own `—`.
    readRequirements: [["partner-credentials:summary:read"]],
  },
  {
    id: "policies",
    glyph: "gavel",
    owners: ["TEN-02"],
    title: "Policies and settings",
    section: "Govern",
    purpose:
      "Cancellation terms, support hours and the other settings that feed your published policy page.",
    // Served in R-39, and it took both halves of what `page-support.test.ts` said was wrong.
    //
    // The row shape: `ConfigurationEntry` is now a published component, so `entries` carries
    // `tenantId`, `namespace`, `key`, `value`, `version`, `createdAt` and `updatedAt` instead of
    // `type: object, additionalProperties: true`. It is the one hand-written schema in that contract
    // and the contract says why — the generator refuses `ConfigurationValue`, a union that refers to
    // itself, rather than approximating it.
    //
    // The operation: typing the row alone would have left this board with nothing to ask for, because
    // the namespace listing's namespace is a required path segment behind a pattern and no operation
    // anywhere publishes a vocabulary of namespaces. So `GET /v1/configuration` answers the tenant
    // across every namespace, paged over `(namespace, key)`, with the namespace demoted to an
    // optional filter a board applies once it can see which namespaces exist.
    //
    // **`Effective` has no source, and `updatedAt` is not it.** The design's third column is
    // `1 Sep 2026` and `from 1 Oct` — when a setting takes effect, and the form's own "Effective
    // from" field. TEN-02 holds no scheduled effective date: `PUT /v1/configuration/{namespace}/{key}`
    // takes a value and an expected version, and `updatedAt` is when the entry last changed.
    // Recorded as Q-101.
    readRequirements: [["tenant-configuration:read"]],
    writeRequirements: [["tenant-configuration:write"]],
  },
  {
    id: "audit",
    glyph: "history",
    railLabel: "Audit",
    owners: ["AUD-01"],
    title: "Tenant audit",
    section: "Govern",
    purpose:
      "Everything your staff and ours did in your account. Platform staff actions are marked.",
    // Built, and the clearest fit of the fifteen. `GET /v1/audit-records` answers a tenant-scoped
    // page of records, newest first, publishing `occurredAt`, `actorChain`, `action`, `outcome`,
    // `subject`, `resource`, `producerSubject`, `correlationId`, `contentHash` and `ingestedAt` —
    // the design's three columns, its state vocabulary, and the one thing that makes this page
    // different from a log viewer: `actorChain[].kind` carries `impersonator`, which is how a
    // platform-staff action on a tenant's own account is marked.
    //
    // One read scope, and the service restricts every row to the verified token tenant rather than
    // to a tenant named in the path — so there is no second workload scope to hold, and no way for
    // this page to ask for somebody else's log.
    readRequirements: [["audit:read"]],
    // No write requirement, and the design's own primary command is why. `POST /v1/audit-records`
    // appends a record; it is not an export, and the design's "Export audit log" form has no
    // operation behind it. A page that asked for `audit:write` would be asking for the permission
    // to write the log it is reading.
  },
  {
    id: "config",
    glyph: "settings",
    owners: ["TEN-02"],
    title: "Configuration",
    section: "Settings",
    purpose:
      "Operational settings. Releases are immutable and versioned: you change, review, publish, and can roll back.",
    // Served in R-39 for its rows, by the same `GET /v1/configuration` as `policies` above, and
    // **this page is the one of the five whose own subject is still absent.** The release machinery
    // the design's whole page is about — an immutable versioned release, a diff, a roll back —
    // belongs to the application-configuration service and is entirely under `/internal/v1`, which
    // ADR 0029 §2 says the gateway refuses at load rather than rewrites. So the grid draws, and its third column, `Release` —
    // `r-0418 · production`, `draft · staging`, `r-0412 → rolled back` — has no source, as do the
    // `Live release`, `Draft changes` and `Rollbacks` tiles and the form's `Environment` field.
    //
    // Reachable rather than blocked because the question `blockedBy` answers is whether the platform
    // serves a list of what the page is a list of, and it now does: these are the tenant's
    // configuration settings. What is missing is a column and a command, recorded as Q-104, in the
    // same shape as `catalogue`'s constant `lifecycle` and `zones`'s absent coverage vocabulary.
    readRequirements: [["tenant-configuration:read"]],
    writeRequirements: [["tenant-configuration:write"]],
  },
  {
    id: "flags",
    glyph: "toggle_on",
    owners: ["CFG-02"],
    title: "Feature flags",
    section: "Settings",
    purpose: "Turn capabilities on and off, for everyone or a share of customers.",
    // Served in R-55 by ADR 0045. Case 1 with case 2 inside it: every operation was internal, and
    // the internal read is by `flagKey` — so even past the gateway there would have been no list of
    // a tenant's flags, which is why the remedy was a listing and not a route. CFG-02 now publishes
    // `GET /v1/features`, the newest version of every flag in the tenant, paged over the key.
    //
    // **`feature-management:read` is not what this page holds.** That scope reads every field of any
    // flag a caller can name, approval evidence and allocation seed included; the listing has its
    // own, `feature-management:summary:read`, and the read requirement here changed accordingly.
    // The seed is the field that matters: it is what CFG-02 hashes with a subject key to decide a
    // percentage rollout, so publishing it would let a browser compute which customers a 25%
    // rollout includes before it reaches them.
    //
    // **A rollout of 0% is not off**, and the board's state readings are `evaluateFeature`'s own
    // order read back rather than a status field — there is no status. `flags.ts` has the six
    // readings and why.
    //
    // **`Expires` has no field anywhere**: one tile, one badge word and one form input. Q-151.
    readRequirements: [["feature-management:summary:read"]],
  },
  {
    id: "plan",
    glyph: "workspace_premium",
    owners: ["TEN-03"],
    title: "Plan and entitlements",
    section: "Settings",
    purpose: "What your business has bought from the platform, and how much of it you use.",
    // Served in R-39. `GET /v1/tenants/{tenantId}/entitlements` answers every entitlement a tenant
    // holds, publishing `capability`, `enabled`, `limit`, `effectiveFrom`, `effectiveUntil`,
    // `version`, `createdAt`, `updatedAt` and a `nextAfter`. The absence it closes is the one with no
    // workaround: an entitlement was readable one `capability` at a time, capabilities are free-form
    // strings, and nothing published a vocabulary of them — so there was no first request this board
    // could make, and `Brand and domains` above records the same dead end from its own side.
    //
    // The listing is deliberately unfiltered by effectiveness. An entitlement that starts next month
    // is part of "what your business has bought", and the by-capability read returns only what is in
    // force now, so a board built on that read could not have shown it at all.
    //
    // **`Used` has no source, and it is half of every row the design draws.** `312 of 500`,
    // `4,610,220 of 5,000,000` — the limit is published and the consumption is not. Nothing on the
    // platform meters a tenant's use of a capability against its entitlement; the `92% used` badge
    // and the `Brands · 3 of 5` and `Drivers · 312 of 500` tiles are the same absence. Nor is the
    // plan itself published: `Plan · Growth`, `annual` and `Renews · 1 Jan 2027` have no field.
    // Recorded as Q-105.
    //
    // No write requirement, and the design's own primary action is why. Its form is `Change plan →
    // Request change`, which asks an account manager for a different plan; the only write here is
    // `PUT …/entitlements/{capability}`, a platform-operator grant. Asking a tenant administrator's
    // role for `tenant:entitlement:write` would be asking for the permission to raise their own
    // limits. The same reasoning as `audit` above.
    readRequirements: [["tenant:entitlement:read"]],
  },
  /*
   * The four analytics pages, which the design file does not draw.
   *
   * Document 00 §19.25 asks for five kinds of analytics by name and the design commission did not
   * cover them, so the gap is between two documents rather than inside one. They are built on
   * DAT-04 (ADR 0094) and marked `commissioned` so that nothing claims they match a design.
   *
   * One page per question rather than one page with four sections, which is this console's own
   * idiom: a page is a board is a question, and each of these wants its own filter, its own
   * columns and its own export.
   */
  {
    id: "analytics-demand",
    glyph: "trending_up",
    owners: ["DAT-04"],
    title: "Demand and revenue",
    railLabel: "Demand",
    section: "Analytics",
    commissioned: true,
    purpose: "What was completed and cancelled over time, and what it was worth.",
    readRequirements: [["analytics:read"]],
  },
  {
    id: "analytics-supply",
    glyph: "two_wheeler",
    owners: ["DAT-04"],
    title: "Supply and utilisation",
    railLabel: "Supply",
    section: "Analytics",
    commissioned: true,
    purpose: "How much driver time there was, and how much of it was working.",
    readRequirements: [["analytics:read"]],
  },
  {
    id: "analytics-retention",
    glyph: "group",
    owners: ["DAT-04"],
    title: "Customer retention",
    railLabel: "Retention",
    section: "Analytics",
    commissioned: true,
    purpose: "Customers grouped by the month they first appeared, and who came back.",
    readRequirements: [["analytics:read"]],
  },
  {
    id: "analytics-quality",
    glyph: "verified",
    owners: ["DAT-04"],
    title: "Operational quality",
    railLabel: "Quality",
    section: "Analytics",
    commissioned: true,
    purpose: "What went wrong over time, and what people said about it.",
    readRequirements: [["analytics:read"]],
  },
  {
    id: "driver-onboarding",
    glyph: "badge",
    // The rail says the short noun and the page keeps the full title, which is the habit the
    // design follows on every group and the commissioned analytics boards copied.
    railLabel: "Drivers",
    // DRV-04 is an owner because this board reads a courier's vehicle and writes one. Adding the
    // scopes without the owner would have derived a grant and no route: the generator builds the
    // console's proxy table from the owners, so the board would have drawn and then 404ed on its
    // own vehicle read. Caught by grepping the generated table before deploying, not by a test.
    owners: ["DRV-01", "DRV-03", "DRV-04"],
    title: "Driver onboarding",
    /*
     * Its own group, not `People`, where a driver would conceptually belong.
     *
     * The rule the analytics four established: a page no design draws is appended in a group of
     * its own rather than interleaved, "so the rail still reads as the design's six groups with an
     * addition rather than as six groups quietly rearranged". Putting this in `People` would make
     * a designed group one item longer than the design draws it, which is the quiet rearrangement
     * that rule exists to prevent.
     */
    section: "Onboarding",
    purpose: "Your couriers and whether their documents let them work.",
    /*
     * **The one page on this console that no design draws, and the reason it exists anyway.**
     *
     * Document 01 §265 says a tenant must be able to "manage drivers". DRV-03 publishes
     * `POST /v1/drivers/{driverId}/documents/{documentId}/verification-decisions` under
     * `driver-document:verify`, and no screen anywhere spent that scope — so under ADR 0032 no
     * client held it, correctly, because a grant is derived from what screens spend and nothing
     * spent this one.
     *
     * What that cost: EXP-003's readiness gate blocks a courier on `no_documents`, so **no
     * signed-in courier could go online, accept an offer or run a trip**, on an estate where
     * every other surface worked. The design specifies the courier's whole side of onboarding and
     * says "We're checking your documents" without saying which screen checks. Recorded as Q-236
     * in `docs/design/DESIGN_QUESTIONS.md`, and this page is the answer being built rather than
     * the question being left open.
     *
     * Because no design draws it, it sits **outside** the 114-screen ratio exactly as the four
     * analytics boards do under Q-186: adding to a numerator what the denominator cannot hold is
     * how a meter lies. `ui/DriverOnboarding.tsx` and `src/driver-onboarding.ts` carry which
     * words are this console's own rather than a design's.
     *
     * Three scopes, and the third is the point: the listing, the documents read, and the decision
     * that nothing else on this platform issues.
     */
    commissioned: true,
    /*
     * The listing takes **both** DRV-01 scopes, and declaring only `driver-profile:read` was wrong:
     * `GET /v1/driver-profiles` enforces `driver-profile:read+driver-profile:workload` together, so
     * the board would have drawn and then answered 403 on its own first read.
     *
     * Caught by `tests/contract/page-scopes-cover-their-listings.test.ts` before it reached the
     * estate, which is the under-grant class ADR 0098 is about arriving from a new direction: a page
     * declaring one scope of a pair looks complete and is not. The dispatch console's roster
     * declares the same pair for the same listing, and the reason is in its comment — neither
     * by-identifier read carries an ownership check, so the read scope alone already reaches any
     * driver in the tenant and what a listing adds is enumeration.
     */
    readRequirements: [
      ["driver-profile:read", "driver-profile:workload"],
      ["driver-document:read"],
      // The vehicle column reads DRV-04 once per driver on every load. Declaring the two writes
      // and not this read is what produced eight 403s the first time the board was driven: the
      // grant derives from what is declared here, and a column that reads is a scope that is spent.
      ["vehicle-assignment:read"],
    ],
    writeRequirements: [
      ["driver-document:verify"],
      ["vehicle:create"],
      ["vehicle-assignment:write"],
    ],
  },
  {
    id: "merchants",
    glyph: "storefront",
    // The rail says the short noun and the page keeps the full title.
    railLabel: "Merchants",
    owners: ["MER-01", "MER-02"],
    /*
     * `Merchant onboarding`, not `Merchants`, and the rename is a measurement fix as much as a
     * naming one. The design draws a `### Merchants` heading — the rider's list of shops to buy
     * from — and the completion meter matches a routed page to a heading **by title**, so a board
     * called `Merchants` claimed the rider's screen and the figure rose on a page the denominator
     * does not contain. It also now obeys the rail rule the analytics boards set: the rail says the
     * short noun, the page keeps the full title.
     */
    title: "Merchant onboarding",
    /*
     * Beside driver onboarding in the commissioned group, not in a designed one.
     *
     * Both pages admit somebody to the tenant — a courier and a shop — and neither is drawn by
     * any design, so they share the rule the analytics four established: appended in a group of
     * their own rather than interleaved, so the rail still reads as the design's six groups with
     * additions rather than as six groups quietly rearranged.
     */
    section: "Onboarding",
    purpose: "The shops trading in your account, and the ones waiting to.",
    /*
     * **No design draws this board, and Document 01 does not say the tenant owns merchants.**
     *
     * §265 lists drivers, vehicles, services, prices, zones, trips and reporting. Merchants are
     * not in it, so unlike the driver-onboarding board beside it this one has no requirements
     * anchor. The decision was taken on the record's own scoping: `merchant.tenantId` makes a
     * merchant a thing inside one tenant, and this console already administers that tenant's
     * catalogue, pricing, zones, staff, customers and drivers. A platform-console board would
     * have been reaching inside one tenant's data from outside it.
     *
     * What it unblocked: MER-01's create was held by no client because no screen spent it, so
     * **no merchant existed in any tenant** — which left the merchant portal's `merchant_id`
     * claim with nothing to point at, its six boards on their empty states, and the rider with
     * nothing to shop from.
     *
     * Four scopes, and three of them were held by nobody before this page: the listing, the
     * create, the status change that lets a shop trade, and MER-02's branch read.
     */
    commissioned: true,
    readRequirements: [["merchant:read"], ["merchant-location:read"]],
    writeRequirements: [["merchant:create"], ["merchant:lifecycle"], ["merchant-location:create"]],
  },
] as const;

/**
 * Whether the granted scopes satisfy any one of the alternatives outright.
 *
 * `every` inside `some`: a partly-held requirement is not partial access to a route, it is a
 * refusal.
 */
function satisfies(
  granted: ReadonlySet<string>,
  requirements: readonly (readonly string[])[],
): boolean {
  return requirements.some((alternative) => alternative.every((scope) => granted.has(scope)));
}

export function resolvePage(page: PageDefinition, granted: ReadonlySet<string>): ResolvedPage {
  // A page the platform cannot support is forbidden to everyone, including an administrator holding
  // every scope in existence. On this console that was eleven of fifteen and is now six, which is
  // still why the rail's foot has something to say.
  if (page.blockedBy !== undefined) return { page, access: "forbidden", visible: false };

  const canRead = satisfies(granted, page.readRequirements);
  const canWrite =
    page.writeRequirements !== undefined && satisfies(granted, page.writeRequirements);

  if (!canRead && canWrite) return { page, access: "full", visible: false };
  if (!canRead) return { page, access: "forbidden", visible: false };
  return { page, access: canWrite ? "full" : "read_only", visible: true };
}

export interface NavigationSection {
  readonly section: string;
  readonly pages: readonly ResolvedPage[];
}

/** Navigation for one administrator, grouped as the design groups it, with empty groups dropped. */
export function buildNavigation(
  granted: ReadonlySet<string>,
  pages: readonly PageDefinition[] = tenantAdminPages,
): NavigationSection[] {
  const sections: NavigationSection[] = [];
  for (const page of pages) {
    const resolved = resolvePage(page, granted);
    if (!resolved.visible) continue;
    const existing = sections.find((candidate) => candidate.section === page.section);
    if (existing === undefined) sections.push({ section: page.section, pages: [resolved] });
    else (existing.pages as ResolvedPage[]).push(resolved);
  }
  return sections;
}

/**
 * Whether an administrator can open a page they navigated to directly.
 *
 * Separate from visibility on purpose: a bookmark, a link in a change notification and a deep link
 * in an alert all arrive without passing through the rail.
 */
export function canOpen(pageId: string, granted: ReadonlySet<string>): boolean {
  const page = tenantAdminPages.find((candidate) => candidate.id === pageId);
  if (page === undefined) return false;
  return resolvePage(page, granted).access !== "forbidden";
}

/** Every scope this console can make use of, for a token request or an audit. */
export function allConsoleScopes(pages: readonly PageDefinition[] = tenantAdminPages): string[] {
  const scopes = new Set<string>();
  for (const page of pages) {
    if (page.blockedBy !== undefined) continue;
    for (const scope of page.readRequirements.flat()) scopes.add(scope);
    for (const scope of (page.writeRequirements ?? []).flat()) scopes.add(scope);
  }
  return [...scopes].sort();
}

/**
 * How many pages this administrator cannot see, and what each would need.
 *
 * Only pages hidden by scope. A page the platform cannot support is invisible to everybody,
 * including an administrator holding every scope, so counting it here would tell them their role is
 * short of something no role has — and on this console it would say six.
 */
export function pagesNotInRole(
  granted: ReadonlySet<string>,
  pages: readonly PageDefinition[] = tenantAdminPages,
): { id: string; title: string; needs: readonly string[] }[] {
  return pages
    .filter((page) => page.blockedBy === undefined && !resolvePage(page, granted).visible)
    .map((page) => ({
      id: page.id,
      title: page.title,
      // The cheapest way in: the alternative with fewest scopes not already held.
      needs:
        [...page.readRequirements]
          .map((alternative) => alternative.filter((scope) => !granted.has(scope)))
          .sort((left, right) => left.length - right.length)[0] ?? [],
    }));
}

/** The pages the platform cannot support yet, and why. */
export function blockedPages(
  pages: readonly PageDefinition[] = tenantAdminPages,
): { id: string; title: string; blockedBy: string }[] {
  return pages
    .filter((page): page is PageDefinition & { blockedBy: string } => page.blockedBy !== undefined)
    .map((page) => ({ id: page.id, title: page.title, blockedBy: page.blockedBy }));
}
